import React from "react";
import { Link } from "react-router-dom";
import { Compass, Shield, Users, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, Eyebrow } from "@/components/Reveal";

const HERITAGE = "https://images.pexels.com/photos/12397217/pexels-photo-12397217.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Mission() {
  return (
    <div>
      <PageHeader
        eyebrow="Our Mission"
        title="Long-term stewardship for those history left unrepresented."
        subtitle="The 7 Generations Indigenous Trust exists to preserve heritage, steward lineage, and build independent prosperity for descendants who possess verified ancestry yet remain outside federal and state tribal frameworks."
      />

      <section className="py-24 bg-ink">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-tight">
              The 7 Generations Philosophy
            </h2>
            <div className="space-y-6 mt-8 font-sans text-[#A19D98] leading-relaxed">
              <p>
                Every decision the Trust makes is weighed against its impact seven generations forward.
                We are not custodians of a single lifetime — we are stewards of an unbroken lineage that
                stretches from the Woodland Mississippian mound-builders to the descendants yet to be born.
              </p>
              <p>
                This is stewardship as a sacred, institutional discipline: patient capital, permanent
                records, and cultural memory protected against the erosion of time and the reach of
                external authority.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-gold/20 overflow-hidden">
              <img src={HERITAGE} alt="Woodland Mississippian heritage relief" className="w-full h-[420px] object-cover" />
            </div>
            <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/50 mt-4">
              Woodland Mississippian & Modern Freedmen Heritage
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-ink-deep">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><Eyebrow>What We Do</Eyebrow></Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              { icon: Shield, title: "Preserve", text: "We permanently record and encrypt genealogies, censuses, and lineage charts on a decentralized registry controlled by the family." },
              { icon: Compass, title: "Steward", text: "We manage lineage records and shared assets across generations with the discipline of a sovereign wealth fund and the intimacy of a family office." },
              { icon: Users, title: "Empower", text: "We issue digital economic citizenship credentials and connect members through mutual-aid networks rooted in shared heritage." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <div className="border border-gold/15 bg-ink p-9 h-full hover:border-gold/40 transition-colors duration-500">
                  <c.icon size={26} className="text-gold" strokeWidth={1.3} />
                  <h3 className="font-serif text-2xl mt-6">{c.title}</h3>
                  <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link to="/institution?tab=philosophy" data-testid="mission-structure-cta" className="group inline-flex items-center gap-3 mt-14 font-mono text-[11px] tracking-[0.2em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold transition-colors">
              Read our institutional structure <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-ink" data-testid="mississippian-map-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Ancestral Homelands</Eyebrow>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-tight mt-6 max-w-3xl">
              The Southeastern Woodlands.
            </h2>
            <p className="font-sans text-[#A19D98] leading-relaxed mt-6 max-w-3xl">
              From roughly 800 to 1600 CE, the Woodland Mississippian peoples flourished across the entire
              Southeastern Woodlands — a vast cultural region reaching from the Atlantic coast of Virginia
              and the Carolinas, across Georgia and all of Florida, through Alabama, Mississippi, Louisiana,
              and eastern Texas, and up the Mississippi, Ohio, and Tennessee river valleys. This academic
              reconstruction traces that ancestral homeland and its principal centers and nations.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <figure className="border border-gold/20 bg-ink-surface mt-12 overflow-hidden">
              <img
                src="/mississippian-map.jpg?v=3"
                alt="Academic historical map of the Southeastern Woodlands cultural region, ancestral homeland of the Woodland Mississippian peoples, spanning the Southeastern United States from the Atlantic coast to eastern Texas"
                data-testid="mississippian-map-image"
                className="w-full h-auto object-contain"
              />
              <figcaption className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold/50 p-6 border-t border-gold/10">
                The Southeastern Woodlands cultural region · c. 800–1600 CE · Nations include Cherokee · Creek · Choctaw · Chickasaw · Natchez · Seminole · Timucua · Powhatan · Catawba · Caddo, and numerous confederations, chiefdoms, townships and coalescence · principal centers: Cahokia · Moundville · Etowah · Ocmulgee
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
